import { Card, Metric, Text, Flex, BadgeDelta } from '@tremor/react';
import { Link } from 'react-router-dom';
import type { DeltaType } from '@tremor/react';
import type { ReactNode } from 'react';

interface Props {
  label: string;
  value: string;
  sub?: string;
  /** Slot below `sub` for richer secondary content — used by the AR tile to
   *  show the aging mini-bar. Falls through cleanly when omitted. */
  extra?: ReactNode;
  delta?: { text: string; type: DeltaType };
  /** When set, the whole tile becomes a click-through to the linked page
   *  (e.g. AR tile → /ar, Inventory tile → /inventory). Adds a hover
   *  affordance so the affordance is discoverable. */
  to?: string;
  /** Compact variant for dense KPI rows (e.g. the 5-up Corte de caja row).
   *  Uses a smaller responsive value that never wraps or overflows the card,
   *  plus tighter padding, instead of the full-size 30px Metric. */
  dense?: boolean;
}

/**
 * Headline metric card. Optionally a Link — keeps clickable KPIs as their
 * own surface rather than a separate Wrapped/Plain split. The hover state
 * is only added when `to` is set so static tiles stay calm.
 */
export default function KpiTile({ label, value, sub, extra, delta, to, dense = false }: Props) {
  const cardCls = [
    to ? 'hover:ring-2 hover:ring-tremor-brand transition-shadow cursor-pointer' : '',
    dense ? '!p-3 sm:!p-4' : '',
  ].filter(Boolean).join(' ');
  const inner = (
    <Card className={cardCls}>
      <Flex alignItems="start">
        <div className="min-w-0 flex-1">
          <Text className={dense ? 'truncate text-xs sm:text-sm' : undefined} title={dense ? label : undefined}>{label}</Text>
          {dense ? (
            // Responsive, non-wrapping value so a long peso figure (e.g.
            // "$1,234,567.89") never overflows or wraps a narrow 5-up card.
            <p className="mt-1 font-semibold tabular-nums tracking-tight whitespace-nowrap text-tremor-content-strong dark:text-dark-tremor-content-strong text-lg sm:text-xl lg:text-2xl">
              {value}
            </p>
          ) : (
            <Metric className="mt-1">{value}</Metric>
          )}
          {sub && <Text className="mt-1 text-tremor-content">{sub}</Text>}
          {extra}
        </div>
        {delta && <BadgeDelta deltaType={delta.type}>{delta.text}</BadgeDelta>}
      </Flex>
    </Card>
  );
  return to ? <Link to={to} aria-label={label}>{inner}</Link> : inner;
}
